/* DayBar — LED-05 */
import { Amount } from '../primitives/Amount';
import styles from './DayBar.module.css';

export interface DayBarProps {
  /** "Today · Sun 16 Aug", "Fri 14 Aug". Written by the caller, because "today" is a client-side idea. */
  readonly label: string;
  /**
   * The running balance at the end of that day, in paise (`FEAT-011 §5.6`).
   *
   * Absent renders the bar without a figure — which is what a filtered view shows while its page is still
   * loading, rather than a zero that would read as a real balance of nothing.
   */
  readonly balanceMinor?: number;
}

/**
 * The day heading on the full ledger (`LED-05`).
 *
 * The screen book calls the running balance on this bar "the single thing a paper ledger does that most
 * expense apps drop", which is why the balance is part of this component rather than an optional extra a
 * screen might forget: a `DayBar` without one is a deliberate state, not an oversight.
 *
 * It is a heading, and marked up as one, so a screen reader can jump between days rather than reading a
 * hundred rows to find the boundary.
 */
export function DayBar({ label, balanceMinor }: DayBarProps): React.JSX.Element {
  return (
    <h3 className={styles.bar}>
      <span className={styles.label}>{label}</span>
      {balanceMinor === undefined ? null : (
        <Amount amountMinor={balanceMinor} size="sm" tone={balanceMinor < 0 ? 'debit' : 'neutral'} />
      )}
    </h3>
  );
}
